import { useState } from "react";
import { readerBlocks, tableRows } from "../lib/readerPresentation";
import { hasDisplayText } from "../lib/displayText";
import { useLocale } from "../lib/LocaleProvider";
import { resolveDocumentTitle } from "../lib/documentTitle";

function blockCitations(block, segment, citations) {
  return (citations || []).filter((item) => {
    if (Number(item.source_segment_sequence) !== Number(segment.sequence)) {
      return false;
    }
    const raw = item.raw_text || "";
    const overlaps =
      Number.isFinite(item.char_start) &&
      Number.isFinite(item.char_end) &&
      item.char_start < block.char_end &&
      item.char_end > block.char_start;
    return raw ? block.text.includes(raw) : overlaps;
  });
}

function CitationActions({
  items,
  onCitationOpen,
  onSourceOpen,
}) {
  const { locale, text, count } = useLocale();
  const [preview, setPreview] = useState(null);
  const [previewItem, setPreviewItem] = useState(null);
  const [busy, setBusy] = useState(false);
  const uniqueItems = [
    ...new Map(
      items.map((item) => [
        item.resolved_target_id ||
          `${item.citation_type}|${item.normalized_text || item.raw_text}`,
        item,
      ]),
    ).values(),
  ];
  const resolved = [
    ...new Map(
      uniqueItems
        .filter((item) => item.resolution_status === "resolved")
        .map((item) => [item.resolved_target_id, item]),
    ).values(),
  ];
  const unresolved = uniqueItems.filter(
    (item) => item.resolution_status !== "resolved",
  );

  return (
    <div className="inline-citation-actions">
      {resolved.map((item) => (
        <button
          key={item.resolved_target_id}
          type="button"
          onClick={async () => {
            setBusy(true);
            setPreviewItem(item);
            const loaded = await onCitationOpen(
              item.resolved_target_expression_id,
              item.resolved_target_work_id,
              item.raw_text,
              item.target_segment_sequence,
              true,
            );
            setPreview(loaded);
            setBusy(false);
          }}
        >
          {busy && previewItem === item
            ? text("reader.loading")
            : text("reader.openCited")}
          <span aria-hidden="true">↗</span>
        </button>
      ))}
      {previewItem && (
        <aside className="inline-citation-popover" aria-live="polite">
          <button
            className="citation-popover-close"
            type="button"
            aria-label={text("common.close")}
            onClick={() => {
              setPreview(null);
              setPreviewItem(null);
            }}
          >
            ×
          </button>
          <span className="citation-popover-label">
            {text("reader.citedDocuments")}
          </span>
          {preview?.document ? (() => {
            const title = resolveDocumentTitle(preview.document, locale);
            return <>
              {title.text
                ? <strong lang={title.language}>{title.text}</strong>
                : <strong>{previewItem.raw_text}</strong>}
              {title.isFallback && title.text && (
                <small lang={locale}>{text(title.language === "th" ? "reader.titleInThai" : "reader.titleInEnglish")}</small>
              )}
            </>;
          })() : <strong>{previewItem.raw_text}</strong>}
          <span className="citation-popover-status">{text("common.source")}</span>
          <div>
            <button
              type="button"
              onClick={() =>
                onCitationOpen(
                  previewItem.resolved_target_expression_id,
                  previewItem.resolved_target_work_id,
                  previewItem.raw_text,
                  previewItem.target_segment_sequence,
                  false,
                  "open",
                )
              }
            >
              {text("reader.openCited")}
            </button>
            <button
              type="button"
              disabled={!preview?.source_item}
              onClick={() => onSourceOpen(preview?.source_item)}
            >
              {text("source.preservedFile")}
            </button>
          </div>
        </aside>
      )}
      {unresolved.length > 0 && (
        <span className="inline-citation-unresolved">
           {text("reader.unresolvedCitations", { count: count(unresolved.length) })}
        </span>
      )}
      {uniqueItems.length === 0 && (
        <span className="inline-citation-unresolved">
          {text("reader.noCitations")}
        </span>
      )}
    </div>
  );
}

function groupedReaderBlocks(blocks) {
  return blocks.reduce((groups, block) => {
    const isNote =
      block.type === "reference_note" || block.type === "amendment_note";
    const previous = groups.at(-1);
    if (isNote && previous?.kind === "notes") {
      previous.blocks.push(block);
      return groups;
    }
    groups.push(
      isNote
        ? { kind: "notes", blocks: [block] }
        : { kind: "text", block },
    );
    return groups;
  }, []);
}

function RelatedNotes({
  blocks,
  segment,
  citations,
  onCitationOpen,
  onSourceOpen,
}) {
  const { text, count } = useLocale();
  const entries = blocks.map((block) => ({
    block,
    citations: blockCitations(block, segment, citations),
  }));
  const mappedCount = new Set(
    entries.flatMap(({ citations: items }) =>
      items.map(
        (item) =>
          item.resolved_target_id ||
          `${item.citation_type}|${item.normalized_text || item.raw_text}`,
      ),
    ),
  ).size;
  const containsAmendment = blocks.some(
    (block) => block.type === "amendment_note",
  );
  const containsReference = blocks.some(
    (block) => block.type === "reference_note",
  );
  const label = containsAmendment && containsReference
    ? text("reader.relatedBoth")
    : containsAmendment
      ? text("reader.relatedAmendment")
      : text("reader.relatedReference");

  return (
    <details
      className={`legal-note ${containsAmendment ? "amendment_note" : "reference_note"}`}
    >
      <summary>
        <span>{label}</span>
        <small>
           {mappedCount > 0
             ? text("reader.documentCount", { count: count(mappedCount) })
             : text("reader.noteCount", { count: count(blocks.length) })}
        </small>
      </summary>
      <div className="legal-note-body legal-note-group">
        {entries.map(({ block, citations: items }, index) => (
          <section key={`${block.char_start}-${index}`}>
            <p>{block.text}</p>
            <CitationActions
              items={items}
              onCitationOpen={onCitationOpen}
              onSourceOpen={onSourceOpen}
            />
          </section>
        ))}
      </div>
    </details>
  );
}

function StructuredTable({ rows }) {
  const { text } = useLocale();
  return (
    <div
      className="legal-table-scroll"
      tabIndex={0}
      role="region"
      aria-label={text("reader.table")}
    >
      <table className="legal-table" aria-label={text("reader.table")}>
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {row.cells.map((cell, cellIndex) => {
                const Cell = cell.header ? "th" : "td";
                return <Cell key={cellIndex}>{cell.text}</Cell>;
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function LegalText({
  segment,
  citations,
  language = "th",
  onCitationOpen,
  onSourceOpen,
  source,
  hideNotes = false,
  notesOnly = false,
}) {
  const { text } = useLocale();
  if (!hasDisplayText(segment)) return null;
  const blocks = readerBlocks(segment);
  const groupedBlocks = groupedReaderBlocks(blocks);
  if (notesOnly && !groupedBlocks.some((group) => group.kind === "notes")) return null;
  if (!blocks.length) {
    return (
      <div className="ocr-fallback" role="status">
        <div>
          <strong>{text("reader.ocrImageTitle")}</strong>
            <p>
            {text("reader.ocrImageText")}
          </p>
          <button type="button" onClick={() => onSourceOpen(source)}>
            {text("source.preservedFile")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`legal-blocks ${language === "en" ? "english-legal-text" : ""}`}
      lang={language}
    >
      {segment.derived_from_ocr && !notesOnly && (
        <div className="ocr-review-notice">
          {text("reader.ocrNotice")}
        </div>
      )}
      {groupedBlocks.map((group, index) => {
        if (group.kind === "notes") {
          if (hideNotes) return null;
          return (
            <RelatedNotes
              key={`notes-${group.blocks[0].char_start}-${index}`}
              blocks={group.blocks}
              segment={segment}
              citations={citations}
              onCitationOpen={onCitationOpen}
              onSourceOpen={onSourceOpen}
            />
          );
        }
        if (notesOnly) return null;
        const block = group.block;
        const rows = tableRows(block);
        if (rows !== null) {
          return (
            <StructuredTable
              key={`${block.char_start}-${index}`}
              rows={rows}
            />
          );
        }
        return (
          <p
            className={`legal-paragraph ${block.type} indent-${block.indent_level || 0}`}
            key={`${block.char_start}-${index}`}
          >
            {block.text}
          </p>
        );
      })}
    </div>
  );
}
